'use client';

import React, { useEffect, useState } from 'react';
import {
  updateMeal,
  setSelectedBucket,
} from '@flysync/shared-redux/slices/userInputSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateFareDetails,
  updateSelectedSSR,
} from '@flysync/shared-redux/slices/flightSearchSlice';
import { useToast } from '@/hooks/use-toast';
import Image from '@/lib/app-image';
import { formatCurrency } from '@/lib/utils';
import mealPlaceholder from '@/assets/ssr/meal/none.svg';
import baggagePlaceholder from '@/assets/ssr/baggage/none.svg';

interface SSRI {
  code: string;
  amount: number;
  desc: string;
  id: string;
  value: string;
  count?: number;
}
interface SSRProductI {
  data: SSRI;
  type: 'meal' | 'baggage';
  activeIndexes: any;
  selected: () => number;
}
const SSRProduct: React.FC<SSRProductI> = ({
  data,
  type,
  activeIndexes,
  selected,
}: SSRProductI) => {
  const { toast } = useToast();
  const dispatch = useDispatch();
  const passenger = useSelector(
    (state: { flightSearch: any }) => state.flightSearch.passenger,
  );
  const selectedSSR = useSelector(
    (state: { flightSearch: any }) => state.flightSearch.selectedSSR,
  );

  const selectedBucket = useSelector(
    (state: { flightSearch: any }) => state.flightSearch.fareDetails,
  );

  // Add this to get the current item's count from Redux state
  const currentItemCount =
    selectedSSR?.[activeIndexes.paxIndex]?.[activeIndexes.i]?.[
      activeIndexes.index
    ]?.[type]?.count || 0;

  const currentItem =
    selectedSSR?.[activeIndexes.paxIndex]?.[activeIndexes.i]?.[
      activeIndexes.index
    ]?.[type];

  const incrementCount = (type: 'meal' | 'baggage', data: SSRI) => {
    const ssrData = JSON.parse(JSON.stringify(selectedSSR));
    if (type === 'baggage' && activeIndexes.index !== 0) {
      toast({
        description: 'Baggage cannot be selected at stops',
        variant: 'warning',
      });
      return;
    }

    const ssrTypeData =
      ssrData[activeIndexes.paxIndex][activeIndexes.i][activeIndexes.index][
        type
      ];
    if (ssrTypeData && Object.keys(ssrTypeData).length === 0) {
      const updatedData = { ...data, count: currentItemCount + 1 };
      ssrData[activeIndexes.paxIndex][activeIndexes.i][activeIndexes.index][
        type
      ] = updatedData;
      dispatch(updateMeal({ ...updatedData, increment: 1 }));
      selected(currentItemCount + 1);
      if (type === 'baggage' && activeIndexes.index === 0) {
        ssrData[activeIndexes.paxIndex][activeIndexes.i] = ssrData[
          activeIndexes.paxIndex
        ][activeIndexes.i].map((item) => ({ ...item, baggage: updatedData }));

        ssrData[activeIndexes.paxIndex][activeIndexes.i] = ssrData[
          activeIndexes.paxIndex
        ][activeIndexes.i].map((item: any, index: number) => {
          if (index != 0) {
            item.baggage = {
              ...item.baggage,
              amount: 0,
            };
          }
          return item;
        });
      }
      const updatedBucket = {
        ...selectedBucket,
        [type]: selectedBucket[type] + updatedData.amount,
        addOns: (selectedBucket?.addOns || 0) + updatedData?.amount,
      };
      dispatch(updateSelectedSSR(ssrData));
      dispatch(updateFareDetails(updatedBucket));
      setSsrData(ssrData);
    } else {
      toast({
        description:
          type === 'meal'
            ? "sorry, you can't select more than one meal(s)"
            : "sorry, you can't select more than one baggage(s)",
        variant: 'info',
      });
    }
  };

  const decrementCount = (type: 'meal' | 'baggage', data: SSRI) => {
    const ssrData = JSON.parse(JSON.stringify(selectedSSR));
    if (type === 'baggage' && activeIndexes.index !== 0) return;

    const ssrTypeData =
      ssrData[activeIndexes.paxIndex][activeIndexes.i][activeIndexes.index][
        type
      ];
    const updatedData = { ...data, count: currentItemCount - 1 };
    if (type === 'baggage' && activeIndexes.index === 0) {
      ssrData[activeIndexes.paxIndex][activeIndexes.i] = ssrData[
        activeIndexes.paxIndex
      ][activeIndexes.i].map((item: any) => ({ ...item, baggage: updatedData }));
    }
    if (ssrTypeData && Object.keys(ssrTypeData).length > 0) {
      ssrData[activeIndexes.paxIndex][activeIndexes.i][activeIndexes.index][
        type
      ] = {};
      dispatch(updateSelectedSSR(ssrData));
      dispatch(updateMeal({ increment: -1 }));
      selected(currentItemCount - 1);
      const updatedBucket = {
        ...selectedBucket,
        [type]:
          selectedBucket[type] > data.amount
            ? selectedBucket[type] - data.amount
            : 0,
        addOns:
          (selectedBucket?.addOns || 0) > data?.amount
            ? (selectedBucket?.addOns || 0) - data?.amount
            : 0,
      };
      dispatch(updateFareDetails(updatedBucket));
      setSsrData(ssrData);
    } else {
      toast({
        description:
          type === 'meal' ? 'Meal not available' : 'Baggage not available',
      });
    }
  };

  const [ssrData, setSsrData] = useState(() =>
    JSON.parse(JSON.stringify(selectedSSR)),
  );

  useEffect(() => {
    const currentAmount =
      ssrData[activeIndexes.paxIndex]?.[activeIndexes.i]?.[
        activeIndexes.index
      ]?.[type]?.amount;
    if (currentAmount !== undefined) {
      dispatch(updateFareSummary(currentAmount));
    }
  }, [
    ssrData[activeIndexes.paxIndex]?.[activeIndexes.i]?.[activeIndexes.index]?.[
      type
    ]?.amount,
    dispatch,
  ]);

  return (
    <div className="w-full rounded-lg border p-2 shadow-md sm:p-3 md:p-4">
      <div className="flex items-start justify-between gap-2 sm:flex-row sm:items-center sm:gap-0">
        <div className="flex w-full items-center sm:w-auto">
          <Image
            src={type === 'meal' ? mealPlaceholder : baggagePlaceholder}
            className="h-12 w-12 rounded object-cover sm:h-16 sm:w-16 md:h-[70px] md:w-[70px]"
            width={70}
            height={70}
            loading="lazy"
            alt={'Product image'}
          />
          <div className="ml-2 sm:ml-3 md:ml-4">
            <p className="text-sm font-semibold sm:text-base">{data?.desc}</p>
            <p className="text-xs text-gray-500 sm:text-sm">
              {formatCurrency(data?.amount)}
            </p>
          </div>
        </div>

        <div className="self-end sm:mt-0 sm:self-auto">
          {/* Update conditions to use currentItemCount */}
          {(currentItemCount == 0 || currentItem?.code != data.code) && (
            <button
              className="w-16 rounded-full border border-primary px-2 py-1 text-xs font-medium text-primary transition-colors hover:bg-blue-100 sm:w-20 sm:px-3 sm:text-sm md:px-4"
              onClick={() => incrementCount(type, data)}
            >
              Add
            </button>
          )}

          {currentItemCount > 0 && currentItem?.code == data.code && (
            <div className="flex w-16 items-center justify-between rounded-full border border-primary sm:w-20">
              <button
                className={`rounded-l-2xl px-1 py-1 text-xs font-bold text-primary hover:bg-blue-100 sm:px-2 sm:text-sm`}
                onClick={() => decrementCount(type, data)}
                disabled={currentItemCount === 0}
              >
                -
              </button>
              <span className="text-xs font-semibold sm:text-sm">
                {currentItemCount || 0}
              </span>

              <button
                className={`rounded-r-2xl px-1 py-1 text-xs font-bold text-primary hover:bg-blue-100 sm:px-2 sm:text-sm`}
                onClick={() => incrementCount(type, data)}
                disabled={currentItemCount >= passenger.length}
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const updateFareSummary = (amount: number) => ({
  type: 'UPDATE_FARE_SUMMARY',
  payload: amount,
});

export default SSRProduct;
